export const reportContent = `# The Complete GitHub Copilot Agent Jumpstart Guide

## Table of Contents
1. Introduction & Philosophy
2. Understanding GitHub Copilot Agent Capabilities
3. Initial Setup & Prerequisites
4. Chat Modes & Agent Selection
5. Project Jumpstart: From Idea to Implementation
6. Workflow Patterns & Best Practices
7. Advanced Agent Usage & Automation
8. Universal Project Structure & Guidelines
9. Chat Section as Your Development Console
10. Working with Different AI Models
11. Troubleshooting & Common Patterns
12. Team Collaboration & Scaling
13. Security & Privacy Considerations
14. Real-World Use Cases & Examples
15. References & Resources

---

## 1. Introduction & Philosophy

This comprehensive guide teaches you how to leverage GitHub Copilot Agent in VS Code to jumpstart ANY project, regardless of technology stack or complexity. Whether you're using advanced models or simpler ones, this guide provides proven patterns and workflows for rapid, high-quality development.

### Core Philosophy
- **Agent-First Development**: Use AI as your primary development partner, not just a code assistant
- **Iterative Refinement**: Build, test, refine in rapid cycles with AI guidance
- **Documentation-Driven**: Maintain clear instructions and guidelines that AI can follow
- **Modular Thinking**: Break complex problems into manageable, AI-friendly chunks
- **Quality Gates**: Establish checkpoints and review processes with AI assistance

## 2. Understanding GitHub Copilot Agent Capabilities

### What the Agent Can Do
- **Code Generation**: Write complete functions, components, and modules from natural language descriptions
- **File Operations**: Create, read, modify, and delete files across your entire workspace
- **Project Scaffolding**: Set up complete project structures with proper configurations
- **Refactoring**: Restructure existing code while maintaining functionality
- **Documentation**: Generate comprehensive documentation, README files, and inline comments
- **Testing**: Create unit tests, integration tests, and test configurations
- **Debugging**: Analyze errors, suggest fixes, and implement solutions
- **Tool Integration**: Use VS Code tasks, terminal commands, and external tools
- **Context Awareness**: Understand your entire codebase and maintain consistency

### Agent Modes in VS Code

#### Ask Mode
- **Purpose**: Questions about code, technology concepts, and general guidance
- **Use When**: You need explanations, suggestions, or advice
- **Output**: Text responses with code examples (manually applied)

#### Edit Mode  
- **Purpose**: Direct code edits across multiple files
- **Use When**: You know exactly what files need changes
- **Output**: Immediate code changes applied to specified files

#### Agent Mode (Autonomous)
- **Purpose**: Complex, multi-step tasks with autonomous planning
- **Use When**: High-level requirements that need AI to determine the approach
- **Output**: Complete implementations with tool usage and iteration

### Tool Ecosystem
- **Built-in Tools**: File operations, terminal commands, workspace search
- **MCP Servers**: External integrations (APIs, databases, services)
- **Extension Tools**: VS Code extension-provided capabilities
- **Custom Tools**: Your own toolsets for specific workflows

## 3. Initial Setup & Prerequisites

### Essential Software
\`\`\`bash
# 1. Install VS Code (latest version)
# Download from: https://code.visualstudio.com/

# 2. Install Node.js (if building web/Node projects)
# Download from: https://nodejs.org/

# 3. Install Git
# Download from: https://git-scm.com/
\`\`\`

### VS Code Extensions Setup
1. **GitHub Copilot** (Core AI assistance)
2. **GitHub Copilot Chat** (Chat interface)
3. **Language-specific extensions** (Python, TypeScript, etc.)
4. **VS Code Speech** (Optional: Voice interactions)

### Copilot Configuration
\`\`\`bash
# Sign in to GitHub Copilot
# Ctrl+Shift+P → "GitHub Copilot: Sign In"

# Enable Agent Mode
# Settings → "chat.agent.enabled" → true

# Configure auto-approval (optional, for trusted environments)
# Settings → "chat.tools.autoApprove" → true (use carefully)
\`\`\`

### Initial Workspace Preparation
\`\`\`markdown
# Create this structure in any new project:
project-root/
├── .github/
│   └── copilot-instructions.md
├── docs/
│   └── README.md
├── src/ (or appropriate source folder)
└── .gitignore
\`\`\`

## 4. Chat Modes & Agent Selection

### Choosing the Right Mode

#### Use Ask Mode When:
- Learning about technologies or concepts
- Getting code explanations
- Seeking best practices advice
- Planning architecture decisions

#### Use Edit Mode When:
- Making specific changes to known files
- Implementing well-defined features
- Fixing bugs in specific locations
- Refactoring specific components

#### Use Agent Mode When:
- Starting new projects from scratch
- Implementing complex features across multiple files
- Migrating between technologies
- Solving problems that require tool usage
- Working with unclear or evolving requirements

### Model Selection Strategy

#### For Fast Tasks (GPT-4o mini, Claude-3 Haiku):
- Code completion and simple edits
- Quick documentation updates
- Basic refactoring tasks
- Simple bug fixes

#### For Complex Tasks (GPT-4o, Claude-3.5 Sonnet):
- Architecture planning
- Complex feature implementation
- Multi-file refactoring
- Integration of new technologies

#### For Reasoning Tasks (o1-preview, o1-mini):
- Algorithm design
- Complex debugging
- Performance optimization
- System design decisions

## 5. Project Jumpstart: From Idea to Implementation

### Phase 1: Idea Crystallization (5-10 minutes)

#### Step 1: Define Your Project
Use this template in chat:
\`\`\`
I want to create a [type of application] that [main purpose]. 
The target users are [user type] who need to [main use case].
Key features should include [list 3-5 core features].
Technology preferences: [languages/frameworks, or "suggest best options"].
\`\`\`

#### Step 2: Get AI-Generated Project Plan
Ask the agent:
\`\`\`
Create a detailed project plan with:
1. Technology stack recommendation
2. Project structure
3. Development phases
4. Key milestones
5. Estimated timeline
\`\`\`

### Phase 2: Project Scaffolding (10-15 minutes)

#### Step 1: Create Project Structure
Agent mode prompt:
\`\`\`
Set up a complete project structure for [your project description].
Include:
- Proper folder organization
- Configuration files
- Package management setup
- Development scripts
- Basic documentation
- Git initialization
\`\`\`

#### Step 2: Generate Core Documentation
\`\`\`
Create comprehensive documentation including:
- README with setup instructions
- Contributing guidelines
- Code style standards
- Architecture overview
- API documentation template
\`\`\`

### Phase 3: MVP Development (Varies by complexity)

#### Iterative Development Pattern:
1. **Feature Definition**: Describe one feature at a time
2. **Implementation**: Let agent mode implement it
3. **Testing**: Review and test the implementation
4. **Refinement**: Request improvements or fixes
5. **Integration**: Ensure it works with existing code

#### Example Feature Request:
\`\`\`
Implement [feature name] that allows users to [functionality].
Requirements:
- [Requirement 1]
- [Requirement 2]
- [Requirement 3]

Follow established patterns in the codebase and ensure:
- Proper error handling
- Input validation
- Responsive design (if applicable)
- Documentation
\`\`\`

### Phase 4: Polish & Deployment (Final phase)

#### Quality Assurance Checklist:
\`\`\`
Review the entire codebase and:
1. Add comprehensive error handling
2. Implement proper logging
3. Add unit tests for core functionality
4. Optimize performance
5. Ensure security best practices
6. Create deployment configuration
7. Write user documentation
\`\`\`

## 6. Workflow Patterns & Best Practices

### The Agent-First Workflow

#### 1. Always Start with Context
Before making any request, ensure the agent understands:
- Project goals and constraints
- Existing codebase patterns
- Technology stack and preferences
- Quality standards and requirements

#### 2. Use Progressive Disclosure
Break complex requests into smaller, manageable chunks:
\`\`\`
# Bad: "Build a complete e-commerce system"
# Good: "Create the user authentication system with login/register functionality"
\`\`\`

#### 3. Establish Guidelines Early
Create a \`.github/copilot-instructions.md\` file:
\`\`\`markdown
---
applyTo: "**"
---
# Project Guidelines

## Coding Standards
- Use TypeScript for all new JavaScript code
- Follow ESLint configuration
- Write JSDoc comments for all functions
- Use meaningful variable names

## Architecture Patterns
- Prefer composition over inheritance
- Use dependency injection
- Implement proper error boundaries
- Follow SOLID principles

## Testing Requirements
- Write unit tests for all business logic
- Use integration tests for API endpoints
- Mock external dependencies
- Aim for 80%+ code coverage
\`\`\`

#### 4. Maintain Conversation Context
Use the chat history effectively:
- Reference previous implementations
- Build upon established patterns
- Maintain consistency across requests

### Quality Gates & Review Process

#### After Each Implementation:
1. **Functionality Review**: Does it work as intended?
2. **Code Quality**: Is it maintainable and well-structured?
3. **Security Check**: Are there any vulnerabilities?
4. **Performance**: Is it efficient?
5. **Documentation**: Is it properly documented?

#### Use These Validation Prompts:
\`\`\`
# Code Review
"Review the recent changes and suggest improvements for:"
- Code quality and maintainability
- Performance optimizations
- Security considerations
- Testing coverage

# Architecture Review
"Analyze the current architecture and suggest:"
- Better separation of concerns
- Improved scalability patterns
- Reduced coupling between components
- Enhanced error handling strategies
\`\`\`

## 7. Advanced Agent Usage & Automation

### Batch Operations
Process multiple files or components at once:
\`\`\`
# Refactoring Example
"Refactor all React components to use TypeScript strict mode:
1. Add proper type definitions
2. Fix any type errors
3. Update imports and exports
4. Ensure all props are properly typed"

# Documentation Example  
"Generate comprehensive JSDoc comments for all functions in the /src directory:
- Include parameter descriptions
- Document return values
- Add usage examples
- Note any side effects"
\`\`\`

### Tool Integration Patterns

#### Terminal Automation:
\`\`\`
"Set up a complete development workflow including:"
- Package installation scripts
- Development server configuration
- Build and test automation
- Deployment scripts
- Git hooks for quality checks
\`\`\`

#### External API Integration:
\`\`\`
"Integrate with [API name] to [functionality]:
1. Set up authentication
2. Create service layer for API calls
3. Implement error handling and retries
4. Add caching for performance
5. Write integration tests"
\`\`\`

### Custom Tool Sets
Define reusable tool collections for common workflows.

## 8. Universal Project Structure & Guidelines

### The Agent-Friendly Project Structure

#### Core Structure Template:
\`\`\`
project-name/
├── .github/
│   ├── copilot-instructions.md      # AI guidance and coding standards
│   ├── workflows/                   # CI/CD automation
│   └── ISSUE_TEMPLATE/             # Issue templates
├── docs/
│   ├── README.md                   # Main documentation
│   ├── API.md                      # API documentation
│   ├── CONTRIBUTING.md             # Contribution guidelines
│   └── ARCHITECTURE.md             # System architecture
├── src/                           # Source code
│   ├── components/                # Reusable components
│   ├── services/                  # Business logic
│   ├── utils/                     # Helper functions
│   ├── types/                     # Type definitions
│   └── tests/                     # Test files
├── config/                        # Configuration files
├── scripts/                       # Build/deployment scripts
├── .gitignore                     # Version control exclusions
├── package.json                   # Dependencies and scripts
└── README.md                      # Quick start guide
\`\`\`

### Universal Configuration Files

#### \`.github/copilot-instructions.md\` Template:
\`\`\`markdown
---
applyTo: "**"
---
# Project Development Guidelines

## Code Quality Standards
- Follow language-specific style guides
- Maximum function length: 50 lines
- Maximum file length: 300 lines
- Use meaningful names for variables and functions
- Write self-documenting code with clear comments

## Architecture Principles
- Single Responsibility Principle
- Dependency Injection where applicable
- Error handling at appropriate levels
- Separation of concerns
- Testability first

## Documentation Requirements
- README for each major module
- Inline comments for complex logic
- API documentation for all endpoints
- Architecture decisions recorded

## Testing Standards
- Unit tests for all business logic
- Integration tests for external dependencies
- End-to-end tests for critical workflows
- Minimum 80% code coverage

## Security Guidelines
- Input validation on all user inputs
- Secure authentication and authorization
- No hardcoded secrets or credentials
- Regular dependency updates
\`\`\`

This guide continues with more detailed sections on chat usage, model selection, troubleshooting, team collaboration, security considerations, and real-world examples. The complete guide provides everything needed to master GitHub Copilot Agent for professional development workflows.`

export const guideStructure = [
  {
    id: 'introduction',
    title: 'Introduction & Philosophy',
    description: 'Core principles of Agent-First Development',
    icon: '🎯',
    content: {
      overview: 'Master AI-powered development with proven patterns and workflows',
      principles: [
        'Agent-First Development',
        'Iterative Refinement', 
        'Documentation-Driven',
        'Modular Thinking',
        'Quality Gates'
      ]
    }
  },
  {
    id: 'capabilities',
    title: 'Agent Capabilities',
    description: 'Understanding what GitHub Copilot Agent can do',
    icon: '🤖',
    content: {
      overview: 'Comprehensive overview of AI assistant capabilities',
      features: [
        'Code Generation',
        'File Operations',
        'Project Scaffolding',
        'Refactoring',
        'Documentation',
        'Testing',
        'Debugging'
      ]
    }
  },
  {
    id: 'setup',
    title: 'Initial Setup',
    description: 'Prerequisites and configuration',
    icon: '⚙️',
    content: {
      overview: 'Get your development environment ready',
      steps: [
        'Install VS Code',
        'Setup Extensions',
        'Configure Copilot',
        'Prepare Workspace'
      ]
    }
  },
  {
    id: 'modes',
    title: 'Chat Modes & Selection',
    description: 'Choosing the right mode for your task',
    icon: '💬',
    content: {
      overview: 'Learn when to use Ask, Edit, or Agent mode',
      modes: [
        'Ask Mode - Questions & Guidance',
        'Edit Mode - Direct Changes',
        'Agent Mode - Autonomous Tasks'
      ]
    }
  },
  {
    id: 'jumpstart',
    title: 'Project Jumpstart',
    description: 'From idea to implementation',
    icon: '🚀',
    content: {
      overview: 'Rapid project development workflow',
      phases: [
        'Idea Crystallization',
        'Project Scaffolding',
        'MVP Development',
        'Polish & Deployment'
      ]
    }
  },
  {
    id: 'workflows',
    title: 'Workflow Patterns',
    description: 'Best practices and quality gates',
    icon: '📋',
    content: {
      overview: 'Proven patterns for consistent results',
      patterns: [
        'Context Management',
        'Progressive Disclosure',
        'Quality Reviews',
        'Iterative Development'
      ]
    }
  }
]

export const quickStartSteps = [
  {
    title: 'Setup Your Environment',
    description: 'Install VS Code, GitHub Copilot, and configure your workspace',
    code: '# Install VS Code extensions\n# Configure Copilot settings\n# Setup workspace structure',
    time: '5 min'
  },
  {
    title: 'Create Project Structure',
    description: 'Use Agent mode to scaffold your complete project',
    code: 'Set up a complete project structure for [your project description].\nInclude:\n- Proper folder organization\n- Configuration files\n- Development scripts',
    time: '10 min'
  },
  {
    title: 'Implement Core Features',
    description: 'Build your MVP with iterative development',
    code: 'Implement [feature name] that allows users to [functionality].\nRequirements:\n- [Requirement 1]\n- [Requirement 2]',
    time: '30+ min'
  },
  {
    title: 'Test and Deploy',
    description: 'Add tests, documentation, and deploy your application',
    code: 'Review the entire codebase and:\n1. Add comprehensive error handling\n2. Implement proper logging\n3. Add unit tests',
    time: '20 min'
  }
]

export const examples = [
  {
    title: 'React Web App',
    description: 'Modern React application with TypeScript and Tailwind',
    prompt: 'Create a modern React web application with TypeScript, Tailwind CSS, and routing. Include components for header, footer, and responsive design.',
    tags: ['React', 'TypeScript', 'Tailwind'],
    difficulty: 'Intermediate'
  },
  {
    title: 'Node.js API',
    description: 'RESTful API with Express, authentication, and database',
    prompt: 'Build a RESTful API using Node.js and Express with JWT authentication, MongoDB integration, and comprehensive error handling.',
    tags: ['Node.js', 'Express', 'MongoDB'],
    difficulty: 'Advanced'
  },
  {
    title: 'Python Data Analysis',
    description: 'Data analysis project with visualization',
    prompt: 'Create a Python data analysis project with pandas, matplotlib, and Jupyter notebooks for exploring and visualizing datasets.',
    tags: ['Python', 'Pandas', 'Jupyter'],
    difficulty: 'Beginner'
  },
  {
    title: 'Vue.js Dashboard',
    description: 'Admin dashboard with charts and real-time data',
    prompt: 'Develop a Vue.js admin dashboard with Chart.js integration, real-time data updates, and responsive design for managing business metrics.',
    tags: ['Vue.js', 'Chart.js', 'Dashboard'],
    difficulty: 'Intermediate'
  }
]
